import { useState } from 'react'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface EmailContactFormProps {
  to?: string
  suggestedSubject?: string
  suggestedSummary?: string
  respond: (result: string) => void | Promise<void>
}

export function EmailContactForm({
  to,
  suggestedSubject,
  suggestedSummary,
  respond,
}: EmailContactFormProps) {
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState(suggestedSubject ?? '')
  const [summary, setSummary] = useState(suggestedSummary ?? '')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')

  const emailValid = EMAIL_RE.test(email.trim())
  const canSubmit = emailValid && subject.trim() && !sending

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSending(true)
    setError('')
    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          subject: subject.trim(),
          details: summary.trim(),
          fromEmail: email.trim(),
        }),
      })
      if (!res.ok) throw new Error('send failed')
      setSent(true)
      respond(JSON.stringify({ email: email.trim(), subject: subject.trim() }))
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className='my-3 flex items-center gap-3 overflow-hidden rounded-49xl bg-white/80 px-4 py-3.5 shadow-[0_2px_16px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#34C759]'>
          <svg className='h-3.5 w-3.5 text-white' fill='currentColor' viewBox='0 0 20 20'>
            <path fillRule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clipRule='evenodd' />
          </svg>
        </div>
        <p className='text-[15px] text-black'>
          Got it! We&apos;ll email you at the address you provided as soon as possible.
        </p>
      </div>
    )
  }

  return (
    <div className='my-3 overflow-hidden rounded-[22px] bg-white/80 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,1)] backdrop-blur-2xl'>
      {/* Header */}
      <div className='flex items-center gap-3.5 px-5 pb-4 pt-5'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-linear-to-br from-[#007AFF] to-[#0055B3] shadow-sm'>
          <span className='text-xl leading-none'>✉️</span>
        </div>
        <div>
          <p className='text-[17px] font-semibold leading-snug text-black'>Contact by email</p>
          <p className='text-[13px] leading-snug text-[#6C6C70]'>
            Leave your details and we&apos;ll email you back shortly.
          </p>
        </div>
      </div>

      {/* Fields */}
      <div className='mx-4 overflow-hidden rounded-[14px] bg-[rgba(120,120,128,0.08)]'>
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>Your email address</p>
          <input
            type='email'
            value={email}
            disabled={sending}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Where we'll reach you"
            className='w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>Subject</p>
          <input
            value={subject}
            disabled={sending}
            onChange={(e) => setSubject(e.target.value)}
            placeholder='What is this regarding?'
            className='w-full bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
        <div className='ml-4 h-px bg-[rgba(60,60,67,0.12)]' />
        <div className='px-4 py-2.5'>
          <p className='mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
            Additional details <span className='normal-case tracking-normal text-[#B0B0B5]'>(optional)</span>
          </p>
          <textarea
            rows={3}
            value={summary}
            disabled={sending}
            onChange={(e) => setSummary(e.target.value)}
            placeholder='Anything else we should know…'
            className='w-full resize-none bg-transparent text-[17px] text-black outline-none placeholder:text-[#C7C7CC] disabled:opacity-40'
          />
        </div>
      </div>

      {error && <p className='mx-5 mt-2 text-[13px] text-[#FF3B30]'>{error}</p>}

      {/* Button */}
      <div className='px-4 pb-5 pt-4'>
        <button
          type='button'
          disabled={!canSubmit}
          onClick={handleSubmit}
          className='w-full rounded-[14px] bg-[rgba(0,0,0,0.07)] py-3.5 text-[17px] font-semibold text-black transition-all active:scale-[0.98] disabled:opacity-40'
        >
          {sending ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  )
}
